import { FC } from "react";
import { getColorStyleClasses } from "@/utils/functions";
import type { ColorThemeType } from "@/utils/types/shared/miscellaneous";

export interface ModalHeaderProps {
  icon?: JSX.Element;
  title?: string;
  subtitle?: string;
  onClose: () => void;
  onSave?: () => Promise<void>;
  isLoading?: boolean;
  confirmText?: string;
  buttons?: JSX.Element;
  colorTheme?: ColorThemeType;
  cancelText?: string;
}

export const ModalHeader: FC<ModalHeaderProps> = ({
  icon,
  title,
  subtitle,
  onClose,
  onSave,
  isLoading = false,
  confirmText = "Save",
  cancelText = "Cancel",
  colorTheme = "blue",
  buttons,
}) => {
  const colorClasses = getColorStyleClasses(colorTheme);

  return (
    <div
      className={`bg-gradient-to-r ${colorClasses.gradientFrom} ${colorClasses.gradientTo} border-b ${colorClasses.border} rounded-t-lg px-6 py-4 shadow-md`}
    >
      <div className="flex flex-col-reverse items-center justify-between gap-2 md:flex-row">
        <div className="flex items-center space-x-3 self-start md:self-auto max-w-[65%]">
          {icon && (
            <div className={`p-2 ${colorClasses.iconBg} rounded-lg`}>
              {icon}
            </div>
          )}
          <div className="max-w-[100%]">
            <h1 className={`text-xl font-semibold truncate ${colorClasses.textColor}`}>
              {title}
            </h1>
            <p className={`text-xs truncate ${colorClasses.subtitleColor}`}>
              {subtitle}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3 self-end md:self-auto">
          <button
            onClick={onClose}
            className={`rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 ${colorClasses.focusRing} text-sm font-medium transition-colors duration-200 focus:ring-offset-2`}
          >
            {cancelText}
          </button>
          {buttons}
          {onSave && (
            <button
              onClick={onSave}
              disabled={isLoading}
              className={`px-4 py-2 ${colorClasses.buttonBg} rounded-lg text-white ${colorClasses.buttonHover} focus:outline-none focus:ring-2 ${colorClasses.focusRing} flex min-w-[80px] items-center justify-center text-sm font-medium transition-colors duration-200 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                </div>
              ) : (
                confirmText
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
